import axios, { AxiosError } from "axios";
import { BACKEND_URL } from "../env";
import { getFeatureDeployHeaders, getTenantKey, refreshCognitoToken, refreshSSOToken } from "./utils";
import {
  BACKEND_AUTH_METHOD,
  clearSessionToken,
  decodeJwtPayload,
  getSessionToken,
  refreshIdToken,
  SessionRevokedError,
  SessionUserChangedError,
} from "./authApi";
import { getAppContextRef } from "./app-context-bridge";
import { getLoggerRef } from "../integrations/logger/logger-bridge";
import { clearAllNotificationQueueDedupe } from "../pages/badge-center/notification-popups/useNotificationQueue";
import { clearDatadogUser } from "./datadog";

export const buildUrl = (url: string) => {
  if (BACKEND_URL.includes("/tenant")) {
    return `${BACKEND_URL}${url}`;
  }
  const domain = getTenantKey();
  const firstPart = domain.split(".praxispro.ai")[0];
  // if it has -dev then remove it
  let tenant = firstPart.replace("-dev", "");
  tenant = tenant.replace("dev.", "");
  return `${BACKEND_URL}tenant/${tenant}/${url}`;
};

export const getToken = () => {
  const token = getSessionToken();
  if (token) {
    return `Bearer ${token}`;
  }
  return null;
};

const getTokenExpiry = (): { exp: number | null; isExpired: boolean | null; secondsUntilExpiry: number | null } => {
  try {
    const token = getSessionToken();
    if (!token) return { exp: null, isExpired: null, secondsUntilExpiry: null };
    const payload = decodeJwtPayload(token);
    const exp: number = payload.exp;
    const nowSeconds = Math.floor(Date.now() / 1000);
    return { exp, isExpired: nowSeconds >= exp, secondsUntilExpiry: exp - nowSeconds };
  } catch {
    return { exp: null, isExpired: null, secondsUntilExpiry: null };
  }
};

// When true, any 401 immediately redirects — refresh already failed or succeeded
// and issued a new token that is also rejected (auth is fundamentally broken).
let authFailed = false;

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  failedQueue = [];
};

// Retry through the originating client so onResponseError stays registered.
// apiClient is defined after this point in the file, but retryRequest is only
// ever called at runtime (never during module init), so it will be initialised
// by the time this executes.
const retryRequest = (config: any): Promise<any> => {
  return apiClient(config);
};

// Assigning window.location while a navigation is already pending cancels and
// restarts it. Every request in flight when the session dies gets a 401, so
// without this guard a burst of them can keep restarting the login page load
// and leave the user on the old, now-empty screen.
let isRedirecting = false;

export const redirectToLogin = () => {
  authFailed = true;
  if (isRedirecting) {
    return;
  }
  isRedirecting = true;
  // Mirror the full cleanup that app-context's removeToken + cognitoUser.signOut() does.
  // We can't call logout() here because it's async (globalSignOut) and we need to navigate immediately.
  localStorage.removeItem("token");
  localStorage.removeItem("id_token");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("auth_method");
  clearSessionToken();
  // Mirror removeToken: drop award-popup dedupe so the next session can replay
  // notifications the poll API still returns.
  clearAllNotificationQueueDedupe();
  // Clear the Cognito SDK's own cached tokens so fetchUserSession on the login
  // page doesn't auto-refresh and redirect back into the app.
  getAppContextRef()?.cognitoUserRef?.current?.signOut();
  clearDatadogUser();
  // replace() so the back button can't return to the torn-down session.
  window.location.replace("/login");
};

const onResponseError = async (error: AxiosError): Promise<any> => {
  const url = error.config?.url;
  const method = error.config?.method?.toUpperCase();
  const status = error.response?.status;

  if (status === undefined) {
    // A real 401 used to reach us without CORS headers (Cognito authorizer
    // rejections and orchestrator error responses didn't attach them), so the
    // browser couldn't read the status and this looked identical to a network
    // error. That's fixed at the source now (API Gateway GatewayResponseDefault4XX/
    // 5XX plus the orchestrator's own error paths always attach CORS headers -
    // PRAXISPRO3-4023/4024), so a genuinely expired/invalid token now arrives
    // here as a real 401 and is handled by the branch below, including a
    // refresh attempt before ever redirecting. A statusless error here is a
    // transient network issue, not an auth failure - log it for visibility but
    // don't tear down a possibly-healthy session over it.
    getLoggerRef()?.warn("API request failed with no response (network error)", {
      url,
      method,
      errorCode: (error as any).code,
      errorMessage: error.message,
      hasToken: !!getSessionToken(),
      authMethod: localStorage.getItem("auth_method"),
      tokenExpiry: getTokenExpiry(),
    });
    return Promise.reject(error);
  }

  if (status !== 401) {
    if (status !== 404) {
      getLoggerRef()?.error("API request failed", error as unknown as Error, {
        url,
        method,
        status,
        responseData: error.response?.data,
        errorCode: (error as any).code,
        errorMessage: error.message,
      });
    }

    return Promise.reject(error);
  }

  // If auth has already permanently failed (refresh failed or new token also rejected),
  // redirect immediately rather than looping.
  if (authFailed) {
    getLoggerRef()?.warn("Auth redirect: authFailed flag already set", { url, method });
    redirectToLogin();
    return Promise.reject(error);
  }

  const originalRequest = error.config as any;

  // If this specific request already went through a refresh+retry and still got 401,
  // the new token is also being rejected — treat as permanent auth failure.
  if (originalRequest._retry) {
    getLoggerRef()?.warn("Auth redirect: refreshed token still rejected by API", { url, method });
    redirectToLogin();
    return Promise.reject(error);
  }
  originalRequest._retry = true;

  const authMethod = localStorage.getItem("auth_method") ?? "cognito";

  // The backend path keeps the refresh token in an HttpOnly cookie, so there is
  // nothing in storage to look for and its absence proves nothing.
  // Empty only on the backend path below, which never reads it.
  const refreshToken = localStorage.getItem("refreshToken") ?? "";
  if (authMethod !== BACKEND_AUTH_METHOD && !refreshToken) {
    getLoggerRef()?.warn("Auth redirect: no refresh token in storage", { url, method });
    redirectToLogin();
    return Promise.reject(error);
  }

  // Another request is already refreshing — queue this one to retry once the new token arrives.
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then((token) => {
      originalRequest.headers["Authorization"] = `Bearer ${token}`;
      return retryRequest(originalRequest);
    });
  }

  isRefreshing = true;
  getLoggerRef()?.warn("Auth: token refresh attempt", { url, method, auth_method: authMethod });

  try {
    const appContext = getAppContextRef();
    const tenantProfile = appContext?.tenantProfile;
    let newToken: string;

    if (authMethod === BACKEND_AUTH_METHOD) {
      newToken = await refreshIdToken();
    } else if (authMethod === "sso") {
      if (tenantProfile?.user_pool_cognito_domain && tenantProfile?.sso_client_id) {
        newToken = await refreshSSOToken(
          tenantProfile.user_pool_cognito_domain,
          tenantProfile.sso_client_id,
          refreshToken
        );
      } else {
        getLoggerRef()?.warn("Auth redirect: SSO tenant config missing for refresh", { url, method });
        processQueue(error, null);
        redirectToLogin();
        return Promise.reject(error);
      }
    } else {
      if (!tenantProfile?.user_pool_id || !tenantProfile?.user_pool_client_id) {
        getLoggerRef()?.warn("Auth redirect: Cognito pool config not in context", { url, method });
        processQueue(error, null);
        redirectToLogin();
        return Promise.reject(error);
      }
      newToken = await refreshCognitoToken(refreshToken, {
        UserPoolId: tenantProfile.user_pool_id,
        ClientId: tenantProfile.user_pool_client_id,
      });
    }

    originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
    processQueue(null, newToken);
    return retryRequest(originalRequest);
  } catch (refreshError) {
    // Logout revoked the session mid-refresh: expected, so no log and no redirect.
    if (refreshError instanceof SessionRevokedError) {
      processQueue(refreshError, null);
      return Promise.reject(refreshError);
    }

    // Another tab signed in as someone else through the shared cookie. Not an
    // auth failure: redirectToLogin would drop the marker and strand this tab
    // on the login page while the cookie is live. A reload restores from it
    // instead, with this tab's token and query cache starting clean.
    if (refreshError instanceof SessionUserChangedError) {
      processQueue(refreshError, null);
      window.location.reload();
      return Promise.reject(refreshError);
    }

    // Refresh itself failed (e.g. 400 from Cognito, expired refresh token) —
    // release queued requests and send user to login.
    getLoggerRef()?.error(
      "Auth redirect: token refresh failed",
      refreshError as Error,
      { url, method, auth_method: authMethod }
    );
    processQueue(refreshError, null);
    redirectToLogin();
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
};

const apiClient = axios.create({
  baseURL: buildUrl(""),
  headers: {
    "Content-Type": "application/json",
    Accept: "*/*",
    ...getFeatureDeployHeaders(),
  },
});

apiClient.interceptors.request.use(
  (config: any) => {
    const token = getToken();
    if (token) {
      config.headers["Authorization"] = token;
    }
    const method = config.method?.toUpperCase() ?? "REQUEST";
    getLoggerRef()?.addBreadcrumb("api", `${method} ${config.url ?? ""}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(undefined, onResponseError);

export default apiClient;
